import { Component, ElementRef, computed, effect, inject, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { SessionContextService } from '../../../core/services/session-context.service';
import { CaseContextService } from '../../../core/services/case-context.service';
import { CaseApiService } from '../../../core/services/case-api.service';
import { CasePickerFlyoutComponent } from '../../../shared/case-box/case-picker-flyout.component';
import { Case, CaseInput, RatingFloor } from '../../../core/services/case.model';
import { RatesFilterStateService, MAX_CASE_FAVORITES } from '../state/rates-filter-state.service';
import { RatesFilterPanelService, type DimKey } from '../state/rates-filter-panel.service';
import { DIM_LABELS, PANEL_DIM_TO_PIN_DIM } from '../state/dim-labels';
import { summarize as summarizeDim } from '../state/dim-summarizers';
import { ProductType } from '../models/rate-sheet.models';
import { RailPinService } from './state/rail-pin.service';
import { AppliedFilterRowComponent } from '../components/applied-filter-row/applied-filter-row.component';
import { InlinePickerHostComponent } from './pickers/inline-picker-host.component';
import { RailFaChecklistPickerComponent } from './pickers/rail-fa-checklist-picker.component';
import { CompanyProductPickerComponent } from '../../../shared/company-product-picker/company-product-picker.component';
import { CarrierProductFlyOutService } from '../../../shared/company-product-picker/carrier-product-flyout.service';
import { RailLayoutService } from '../../../shell/rail-host/rail-layout.service';
import type {
  PickerApplyResult,
  PickerCarrier,
  PickerData,
  PickerProduct,
} from '../../../shared/company-product-picker/company-product-picker.types';
import type { CompanyFilterValue } from '../state/rates-filter-state.service';
import { AM_BEST_OPTIONS, PREMIUM_TYPE_OPTIONS, RIDER_OPTIONS, STATES_LIST } from '../config/filter-options';
import type { DimKey as PinDimKey } from './state/dim-tab-applicability';
import { sortOptionValues } from './state/option-sort';
// Side-effect imports: register picker implementations so lookupPicker()
// resolves when InlinePickerHostComponent looks up 'am-best' / 'prem-type' etc.
import './pickers/flat-checklist-picker.component';
import './pickers/numeric-picker.component';
// Imported for its value, not just its registry side effect: the State
// Availability group renders it directly as its body.
import { StateGridPickerComponent } from './pickers/state-grid-picker.component';
import './pickers/min-premium-range-picker.component';
import './pickers/protection-type-segmented-picker.component';
import { RILA_BUFFER_OPTIONS, RILA_FLOOR_OPTIONS, RILA_RATE_TYPE_OPTIONS } from '../config/filter-options';
import { durationLabel } from '../state/rila-va-derived';

interface PillTab {
  id: ProductType;
  label: string;
}

interface AppliedFilter {
  dim: DimKey;
  label: string;
  summary: string;
  count: number | null;
  pinned: boolean;
}

/**
 * Menu-axis rail — UNIFIED summary + picker surface (2026-05-30).
 */
@Component({
  selector: 'app-rates-rail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AppliedFilterRowComponent,
    InlinePickerHostComponent,
    RailFaChecklistPickerComponent,
    StateGridPickerComponent,
    CompanyProductPickerComponent,
    CasePickerFlyoutComponent,
  ],
  templateUrl: './rates-rail.component.html',
  styleUrl: './rates-rail.component.scss',
})
export class RatesRailComponent {
  protected readonly filterState = inject(RatesFilterStateService);
  protected readonly filterPanel = inject(RatesFilterPanelService);
  protected readonly pinSvc = inject(RailPinService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly flyoutSvc = inject(CarrierProductFlyOutService);
  private readonly railLayout = inject(RailLayoutService);
  private readonly caseCtx = inject(CaseContextService);
  private readonly caseApi = inject(CaseApiService);
  private readonly router = inject(Router);

  protected readonly caseName = computed(() => this.caseCtx.activeCase()?.name ?? '');
  protected readonly caseLoaded = computed(() => !!this.caseCtx.activeCase());
  protected readonly criteriaOpen = signal(true);
  toggleCriteria(): void { this.criteriaOpen.update((v) => !v); }

  private readonly userGroup = computed<number | null>(() => this.sessionCtx.context()?.userGroup ?? null);
  private readonly isBrokerAudience = computed<boolean>(() => {
    const g = this.userGroup();
    return g === 1 || g === 3;
  });
  protected readonly showCriteria = computed<boolean>(() =>
    this.sessionCtx.isBeaconAdmin() || this.userGroup() == null || this.isBrokerAudience());

  readonly statesList = STATES_LIST;
  private readonly AM_BEST_ORDER = ['A++', 'A+', 'A', 'A-', 'B++', 'B+', 'B', 'B-', 'C++', 'C+', 'C', 'C-', 'D'];

  private applyNow(): void { this.filterState.requestApplyFilters(); }

  private numOrNull(v: unknown): number | null {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  protected readonly caseViewMode = computed(() => this.caseCtx.caseViewMode());
  protected readonly caseAllMode = computed(() => this.caseCtx.caseViewMode() === 'all');
  setCaseFit(): void { this.caseCtx.setCaseViewMode('fit'); }
  setCaseAll(): void { this.caseCtx.setCaseViewMode('all'); }

  protected readonly favoriteCount = computed<number>(() => this.filterState.caseFavoriteCount());
  protected readonly favoriteMax = MAX_CASE_FAVORITES;

  get kState(): string {
    this.filterState.version();
    if (this.caseAllMode()) return this.caseCtx.activeCase()?.state ?? '';
    return this.filterState.selectedStates[0] ?? '';
  }
  set kState(v: string) { this.filterState.selectedStates = v ? [v] : []; this.applyNow(); }

  get kPremium(): number | null {
    this.filterState.version();
    if (this.caseAllMode()) return this.caseCtx.activeCase()?.premium ?? null;
    return this.filterState.contributionAmount;
  }
  set kPremium(v: number | null) { this.filterState.contributionAmount = this.numOrNull(v); this.applyNow(); }

  get kTermMin(): number | null {
    this.filterState.version();
    if (this.caseAllMode()) return this.caseCtx.activeCase()?.termMin ?? null;
    return this.surrRange().min;
  }
  set kTermMin(v: number | null) { this.setSurrRange(this.numOrNull(v), this.surrRange().max); }

  get kTermMax(): number | null {
    this.filterState.version();
    if (this.caseAllMode()) return this.caseCtx.activeCase()?.termMax ?? null;
    return this.surrRange().max;
  }
  set kTermMax(v: number | null) { this.setSurrRange(this.surrRange().min, this.numOrNull(v)); }

  get kRating(): string {
    this.filterState.version();
    if (this.caseAllMode()) return (this.caseCtx.activeCase()?.ratingFloor as string | null) ?? '';
    return this.ratingFloorFromSet(this.filterState.selectedAmBest) ?? '';
  }
  set kRating(v: string) { this.filterState.selectedAmBest = v ? this.amBestAtOrAbove(v) : []; this.applyNow(); }

  private surrRange(): { min: number | null; max: number | null } {
    const nums = this.filterState.selectedSurrYears
      .map((s) => parseInt(s, 10)).filter((n) => !Number.isNaN(n));
    if (!nums.length) return { min: null, max: null };
    return { min: Math.min(...nums), max: Math.max(...nums) };
  }
  private setSurrRange(min: number | null, max: number | null): void {
    if (min == null && max == null) { this.filterState.selectedSurrYears = []; this.applyNow(); return; }
    const lo = min ?? max as number, hi = max ?? min as number;
    const years: string[] = [];
    for (let y = Math.min(lo, hi); y <= Math.max(lo, hi); y++) years.push(String(y));
    this.filterState.selectedSurrYears = years;
    this.applyNow();
  }

  private amBestAtOrAbove(floor: string): string[] {
    const i = this.AM_BEST_ORDER.indexOf(floor);
    return i < 0 ? [floor] : this.AM_BEST_ORDER.slice(0, i + 1);
  }
  private ratingFloorFromSet(set: ReadonlyArray<string>): string | null {
    let best = -1;
    for (const r of set) { const i = this.AM_BEST_ORDER.indexOf(r); if (i > best) best = i; }
    return best >= 0 ? this.AM_BEST_ORDER[best] : null;
  }

  protected readonly hasAnyCriteria = computed<boolean>(() => {
    this.filterState.version();
    return !!this.kState || this.kPremium != null
      || this.kTermMin != null || this.kTermMax != null || !!this.kRating;
  });

  protected readonly caseModified = computed<boolean>(() => {
    const c = this.caseCtx.activeCase();
    if (!c) return false;
    this.filterState.version();
    return (c.state ?? '') !== this.kState
      || (c.premium ?? null) !== this.kPremium
      || (c.termMin ?? null) !== this.kTermMin
      || (c.termMax ?? null) !== this.kTermMax
      || (((c.ratingFloor as string | null) ?? '')) !== this.kRating;
  });

  protected readonly fitCount = computed<number>(() => this.filterState.caseFitCount());
  protected readonly totalCount = computed<number>(() => {
    this.filterState.dataVersion();
    return this.filterState.rowCountForTab(this.activeTab());
  });

  resetToCase(): void { this.filterState.requestSeedFromCase(); }

  clearCriteria(): void {
    this.filterState.selectedStates = [];
    this.filterState.contributionAmount = null;
    this.filterState.selectedSurrYears = [];
    this.filterState.selectedAmBest = [];
    this.applyNow();
  }

  protected readonly isPersona = computed<boolean>(() => {
    const c = this.caseCtx.activeCase();
    return !!c && c.clientId == null;
  });

  protected readonly caseList = signal<Case[]>([]);
  protected readonly boundCases = computed(() => this.caseList().filter(c => c.clientId != null));
  protected readonly personaCases = computed(() => this.caseList().filter(c => c.clientId == null));
  protected readonly activeCaseId = computed<number | null>(() => this.caseCtx.activeCase()?.id ?? null);

  private loadCaseList(): void {
    this.caseApi.list().subscribe({ next: (rows) => this.caseList.set(rows) });
  }

  onCasePick(id: number | null): void {
    if (id == null) { this.caseCtx.clear(); return; }
    if (id === this.caseCtx.activeCase()?.id) return;
    this.caseApi.get(id).subscribe({ next: (c) => this.caseCtx.setActive(c) });
  }

  editCase(): void {
    const id = this.caseCtx.activeCase()?.id;
    if (id == null) return;
    this.router.navigate(['/broker/cases', id], { queryParams: { returnTo: this.router.url } });
  }

  saveToCase(): void {
    const c = this.caseCtx.activeCase();
    if (!c) return;
    this.caseApi.update(c.id, this.criteriaToInput(c.name, c.label)).subscribe({
      next: () => this.caseApi.get(c.id).subscribe((fresh) => this.caseCtx.setActive(fresh)),
    });
  }

  saveAsPersona(): void {
    const name = (window.prompt('Name this persona', 'New persona') ?? '').trim();
    if (!name) return;
    this.caseApi.createPersona(name, this.criteriaToInput(name)).subscribe({
      next: ({ id }) => this.caseApi.get(id).subscribe((fresh) => this.caseCtx.setActive(fresh)),
    });
  }

  promoteToCase(): void {
    const c = this.caseCtx.activeCase();
    if (!c) return;
    const clientName = (window.prompt('Client name for this case', '') ?? '').trim();
    if (!clientName) return;
    this.caseApi.promote(c.id, clientName, this.criteriaToInput(clientName)).subscribe({
      next: () => this.caseApi.get(c.id).subscribe((fresh) => this.caseCtx.setActive(fresh)),
    });
  }

  private criteriaToInput(name: string, label?: string | null): CaseInput {
    const r = this.surrRange();
    return {
      name,
      state: this.kState || null,
      premium: this.kPremium,
      termMin: r.min,
      termMax: r.max,
      ratingFloor: (this.kRating || null) as RatingFloor,
      label: label ?? undefined,
    };
  }

  protected readonly tabs: ReadonlyArray<PillTab> = [
    { id: 'IA',   label: 'IA' },
    { id: 'FA',   label: 'FA' },
    { id: 'RILA', label: 'RILA' },
  ];

  protected readonly activeTab = this.filterState.activeTab;

  onPillClick(t: ProductType): void {
    if (this.activeTab() === t) return;
    this.filterState.requestTabSwitch(t);
  }

  /** Scheduled-change count for a pill. Returns 0 when that tab's feed has not
   *  loaded yet, which the template treats as "no badge" — an unloaded tab must
   *  not read as "nothing scheduled". */
  protected changeCount(t: ProductType): number {
    return this.filterState.changeCounts()[t] ?? 0;
  }

  // ── Float changed rows to the top ─────────────────────────────────
  protected readonly floatChanges = this.filterState.floatChanges;

  onToggleFloat(): void {
    this.filterState.toggleFloatChanges();
  }

  protected readonly sessionCtx = inject(SessionContextService);

  /** Should the Products scope toggle render at all?
   *
   *  Shown for 'all' and 'd_shelf', hidden for 'shelf':
   *    'shelf'   → locked to shelf products; no choice, so no control.
   *    'all'     → shown, defaulting to All.
   *    'd_shelf' → shown, defaulting to My Shelf.
   *  Both visible cases honour the toggle (see isShelfActive), so it is never a
   *  dead control.
   *
   *  Enforcement is unaffected either way: isShelfActive() still returns true
   *  for 'shelf', so the grid stays shelf-scoped without the control.
   *
   *  Per-tab by design — a user can be 'shelf' on IA and 'd_shelf' on FA, so
   *  this re-evaluates on tab switch (activeTab is a signal). The no-session
   *  case keeps the disabled affordance so the dev flow still shows it.
   *
   *  Reads shelfAccessFor via shouldShowToggle, which prefers the user-level
   *  shelfPermission* flag from `beacon_dev_user` over the company map. */
  protected readonly showShelfScope = computed(() =>
    !this.sessionCtx.hasSession() || this.sessionCtx.shouldShowToggle(this.activeTab()));

  protected readonly shelfState = computed<'off' | 'on' | 'locked' | 'disabled'>(() => {
    if (!this.sessionCtx.hasSession()) return 'disabled';
    if (this.sessionCtx.shouldForceShelf(this.activeTab())) return 'locked';
    return this.sessionCtx.shelfMode() ? 'on' : 'off';
  });

  onShelfClick(): void {
    const s = this.shelfState();
    if (s === 'locked' || s === 'disabled') return;
    this.filterState.requestShelfToggle();
  }

  protected readonly filtersOpen = signal(false);
  protected readonly pinnedInactiveOpen = signal(false);
  protected readonly carrierSubOpen = signal(false);
  toggleCarrierSub(): void { this.carrierSubOpen.update(v => !v); }
  toggleFilters(): void { this.filtersOpen.update(v => !v); }
  togglePinnedInactive(): void { this.pinnedInactiveOpen.update(v => !v); }

  private readonly dimToGroup: Partial<Record<DimKey, string>> = {
    carrier: 'carrier', amBest: 'carrier', premiumType: 'carrier',
    index: 'crediting', crediting: 'crediting', combo: 'crediting', mktg: 'crediting',
    premium: 'surrenders', surrYears: 'surrenders', faRateTerm: 'surrenders', faSurrYears: 'surrenders', faBonusYears: 'surrenders',
    riders: 'riders', bonus: 'riders', inverse: 'riders', mva: 'riders',
    rop: 'riders', owner: 'riders', annuitant: 'riders', faMva: 'riders', faQualified: 'riders',
    states: 'states',
  };

  private readonly groupOpen = signal<Record<string, boolean>>({
    carrier: false, crediting: false, surrenders: false, riders: false, states: false,
  });

  isGroupOpen(g: string): boolean { return this.groupOpen()[g] ?? false; }
  toggleGroup(g: string): void { this.groupOpen.update(o => ({ ...o, [g]: !o[g] })); }

  expandGroupContaining(dim: DimKey): void {
    const group = this.dimToGroup[dim];
    if (!group) return;
    this.groupOpen.update(o => ({ ...o, [group]: true }));
    if (!this.filtersOpen()) this.filtersOpen.set(true);
  }

  scrollDimIntoView(dim: DimKey): void {
    const el = this.host.nativeElement.querySelector(`[data-dim="${dim}"]`);
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  protected readonly activeGroups = computed<Set<string>>(() => {
    const set = new Set<string>();
    for (const r of this.activeAppliedFilters()) {
      const g = this.dimToGroup[r.dim];
      if (g) set.add(g);
    }
    const slot = this.filterState.activeSlot();
    if (slot.selectedBufferLevels.length || slot.selectedFloorLevels.length
        || slot.selectedSegmentDurations.length || slot.selectedRateTypes.length
        || slot.selectedProtectionType) set.add('crediting');
    if (slot.minPremiumMin != null || slot.minPremiumMax != null) set.add('surrenders');
    if (slot.filterBailOut) set.add('riders');
    return set;
  });

  groupActive(group: string): boolean { return this.activeGroups().has(group); }

  groupBadge(group: string): string {
    this.filterState.version();
    const parts: string[] = [];
    for (const r of this.activeAppliedFilters()) {
      if (this.dimToGroup[r.dim] === group && r.summary) parts.push(r.summary);
    }
    if (group === 'crediting') {
      for (const k of ['buffer-level', 'floor-level', 'segment-duration', 'rate-type', 'protection-type']) {
        const s = this.summaryForRilaVa(k);
        if (s && s !== 'All') parts.push(s);
      }
    } else if (group === 'surrenders') {
      const s = this.summaryForRilaVa('min-premium');
      if (s && s !== 'All') parts.push(s);
    } else if (group === 'riders' && this.filterState.activeSlot().filterBailOut) {
      parts.push('Bail Out');
    }
    return parts.length ? parts.join(' · ') : 'All';
  }

  protected summaryFor(dim: DimKey): string {
    this.filterState.version();
    const slot = this.filterState.activeSlot();
    return summarizeDim(dim, slot, {
      faRateTerms: this.filterState.faRateTerms(),
      faSurrYears: this.filterState.faSurrYears(),
      faBonusYears: this.filterState.faBonusYears(),
      faMva: this.filterState.faMva(),
      faQualified: this.filterState.faQualified(),
    });
  }

  protected summaryForRilaVa(dimKey: string): string {
    this.filterState.version();
    const slot = this.filterState.activeSlot();
    const summarizeMulti = (arr: ReadonlyArray<string>): string => {
      if (arr.length === 0) return 'All';
      if (arr.length === 1) return arr[0];
      if (arr.length === 2) return `${arr[0]}, ${arr[1]}`;
      return `${arr[0]} +${arr.length - 1}`;
    };
    switch (dimKey) {
      case 'buffer-level':     return summarizeMulti(slot.selectedBufferLevels);
      case 'floor-level':      return summarizeMulti(slot.selectedFloorLevels);
      case 'segment-duration': return summarizeMulti(slot.selectedSegmentDurations);
      case 'rate-type':        return summarizeMulti(slot.selectedRateTypes);
      case 'protection-type':  return slot.selectedProtectionType ?? 'All';
      case 'min-premium': {
        if (slot.minPremiumMin == null && slot.minPremiumMax == null) return 'All';
        const lo = slot.minPremiumMin != null ? '$' + slot.minPremiumMin.toLocaleString() : '–';
        const hi = slot.minPremiumMax != null ? '$' + slot.minPremiumMax.toLocaleString() : '–';
        return `${lo}–${hi}`;
      }
      default: return 'All';
    }
  }

  protected readonly isRilaOrVa = computed<boolean>(() => {
    const t = this.activeTab();
    return t === 'RILA' || t === 'VA';
  });

  // Index / Strategy Combo / Mktg Name / Strategy Name are indexed-crediting
  // concepts with no FA equivalent (confirmed against the rate feed's FA
  // mapping, the Benefit API product-search schema, and the custom-reports
  // FA mapper — none carry an index/marketing-name/strategy-name field).
  protected readonly isNotFa = computed<boolean>(() => this.activeTab() !== 'FA');

  // A.M. Best: with NO carrier selected, show the full canonical rating scale
  // (parity with the Products page, which always lists every rating). Once a
  // carrier/product is selected, narrow to the ratings actually present under
  // that selection, ordered by the canonical scale.
  protected readonly amBestOptionsFn = (): string[] => {
    this.filterState.dataVersion();
    this.filterState.companyFilterRequest();
    const cf = this.filterState.getCompanyFilterForTab(this.activeTab());
    const scoped = !!cf && (cf.companies.length > 0 || cf.products.length > 0);
    if (!scoped) return [...AM_BEST_OPTIONS];
    const present = new Set(this.distinctFromActiveTab('amBest'));
    return AM_BEST_OPTIONS.filter((o) => present.has(o));
  };
  protected readonly premTypeOptionsFn = (): string[] => [...PREMIUM_TYPE_OPTIONS];
  protected readonly indexOptionsFn = (): string[] => this.distinctFromActiveTab('indexName');
  protected readonly comboOptionsFn = (): string[] => this.distinctFromActiveTab('strategyCombo');
  protected readonly mktgOptionsFn  = (): string[] => this.distinctFromActiveTab('mktgName');
  protected readonly stratOptionsFn = (): string[] => this.distinctFromActiveTab('strategyName');
  // Product-level surrender years, stamped onto rows from the Benefit API
  // catalog. Was 'surrender', which is round(cap_lock_term / 12) — the cap
  // lock term, not the surrender charge period.
  protected readonly surrOptionsFn  = (): string[] => this.distinctFromActiveTab('surrYearsList');
  protected readonly emptyOptions   = (): string[] => [];
  protected readonly ridersOptionsFn = (): string[] => [...RIDER_OPTIONS];
  protected readonly bufferLevelOptionsFn = (): string[] => RILA_BUFFER_OPTIONS.map((n) => `${n}%`);
  protected readonly floorLevelOptionsFn = (): string[] => RILA_FLOOR_OPTIONS.map((n) => `${n}%`);
  // `segmentDuration` is the feed's rate_term (years). The distinct values come
  // back as numeric strings already sorted shortest-first (sortOptionValues),
  // and are shown as "1 Year" / "6 Years".
  protected readonly segmentDurationOptionsFn = (): string[] =>
    this.distinctFromActiveTab('segmentDuration').map((v) => durationLabel(Number(v)));
  protected readonly rateTypeOptionsFn = (): string[] => [...RILA_RATE_TYPE_OPTIONS];
  protected readonly protectionTypeOptionsFn = (): string[] => ['Buffer Only', 'Floor Only', 'Both'];

  protected readonly togglesActive = computed(() => {
    this.filterState.version();
    const slot = this.filterState.activeSlot();
    return {
      rop: slot.filterRop, bail: slot.filterBailOut, bonus: slot.filterBonus,
      inverse: slot.filterInverse, owner: slot.filterOwner, annuitant: slot.filterAnnuitant,
      faMva: this.filterState.faMva(), faQualified: this.filterState.faQualified(),
    };
  });

  onRopClick(): void         { this.filterState.onRopToggle();         this.filterState.requestApplyFilters(); }
  onBailClick(): void        { this.filterState.onBailOutToggle();     this.filterState.requestApplyFilters(); }
  onBonusClick(): void       { this.filterState.onBonusToggle();       this.filterState.requestApplyFilters(); }
  onInverseClick(): void     { this.filterState.onInverseToggle();     this.filterState.requestApplyFilters(); }
  onFaMvaClick(): void       { this.filterState.setFaMva(!this.filterState.faMva());             this.filterState.requestApplyFilters(); }
  onFaQualifiedClick(): void { this.filterState.setFaQualified(!this.filterState.faQualified()); this.filterState.requestApplyFilters(); }

  /** Distinct non-empty values of `field` across the active tab's rows,
   *  SCOPED to the current Carriers selection (selected companies + their
   *  selected products). So Index / Mktg Name / Strategy Name / Surrender
   *  Years / A.M. Best only offer values that actually exist under the
   *  carriers the user picked — when nothing is picked, the full tab universe.
   *  Reads dataVersion()/companyFilterRequest() so the picker's allOptions
   *  computed re-runs when data loads or the carrier selection changes. */
  private distinctFromActiveTab(field: string): string[] {
    this.filterState.dataVersion();
    this.filterState.companyFilterRequest();
    const cf = this.filterState.getCompanyFilterForTab(this.activeTab());
    const scoped = !!cf && (cf.companies.length > 0 || cf.products.length > 0);
    const companies = scoped ? new Set(cf!.companies) : null;
    const products = scoped && cf!.products.length > 0 ? new Set(cf!.products) : null;
    const set = new Set<string>();
    for (const row of this.filterState.mainRowData) {
      const r = row as Record<string, unknown>;
      if (companies) {
        const co = String(r['company'] ?? r['companyName'] ?? '');
        if (!companies.has(co)) continue;
        if (products) {
          const pr = String(r['product'] ?? r['productName'] ?? '');
          if (pr && !products.has(pr)) continue;
        }
      }
      const v = r[field];
      if (v == null || v === '') continue;
      // A row field can hold several values — a product sold in more than one
      // surrender length carries them all — and each is its own option.
      if (Array.isArray(v)) {
        for (const item of v) {
          if (item == null || item === '') continue;
          set.add(String(item));
        }
        continue;
      }
      set.add(String(v));
    }
    return sortOptionValues(Array.from(set));
  }

  protected readonly companyFilterSummary = computed<{ label: string; count: number | null }>(() => {
    this.filterState.version();
    this.filterState.companyFilterRequest();
    const tab = this.activeTab();
    const cf = this.filterState.getCompanyFilterForTab(tab);
    if (!cf || (cf.companies.length === 0 && cf.products.length === 0)) {
      return { label: 'All', count: null };
    }
    const c = new Set(cf.companies).size;
    const p = cf.products.length;
    if (c > 0 && p === 0) return { label: `${c} ${c === 1 ? 'company' : 'companies'}`, count: c };
    if (c === 0 && p > 0) return { label: `${p} ${p === 1 ? 'product' : 'products'}`, count: p };
    return { label: `${c} ${c === 1 ? 'company' : 'companies'} · ${p} ${p === 1 ? 'product' : 'products'}`, count: c };
  });

  // cf.products holds bare product names (no carrier scoping — see
  // CompanyFilterValue), so reconstruct picker-internal composite ids by
  // cross-referencing which of the SELECTED companies actually carry that
  // product name in pickerData(). This keeps re-seeding the picker (e.g. on
  // re-open) from marking a same-named product selected under a carrier the
  // user never checked.
  protected readonly currentSelectedProductIds = computed<string[]>(() => {
    this.filterState.version();
    this.filterState.companyFilterRequest();
    const tab = this.activeTab();
    const cf = this.filterState.getCompanyFilterForTab(tab);
    if (!cf) return [];
    const productNames = new Set(cf.products);
    const selectedCompanies = new Set(cf.companies);
    const out: string[] = [];
    for (const carrier of this.pickerData().carriers) {
      if (!selectedCompanies.has(carrier.id)) continue;
      for (const p of carrier.products) {
        if (productNames.has(p.name)) out.push(p.id);
      }
    }
    return out;
  });

  // Sourced from the full product catalog for the active tab (not just rows
  // with live rate-feed data) so /rates carrier counts match /products —
  // see RatesFilterStateService.setCatalogProductsForTab (2026-07-13).
  //
  // Carriers are grouped by their ACTUAL company name (the catalog's
  // companyNa field), NOT a brand alias — each distinct legal company is its
  // own row. Product ids are namespaced per-company (`${company}::${product}`)
  // so two carriers that market an identically-named product don't share a
  // selection-Set key (checking one wouldn't select the other).
  protected readonly pickerData = computed<PickerData>(() => {
    this.filterState.dataVersion();
    const tab = this.filterState.activeTab();
    // "My Shelf" narrows the carrier/product tree to the user's approved shelf,
    // so the rail's counts and per-carrier product fly-outs describe the same
    // universe the grid is showing rather than the whole catalog. Reading these
    // signals here is what makes the tree recompute on the shelf toggle.
    // isShelfActive encodes all three permission values: 'all' → never scoped,
    // 'shelf' → always, 'd_shelf' → per the My Shelf toggle.
    const approvedOnly = this.sessionCtx.isShelfActive(tab);
    const companyProducts = new Map<string, Set<string>>();
    for (const row of this.filterState.getCatalogProductsForTab(tab)) {
      const co = row.company;
      const pr = row.product;
      if (!co) continue;
      // Per-pair productType, not the tab's: the Structured tab's catalog merges
      // RILA and VA products whose approved lists are disjoint.
      // A catalog pair with no id can't be shelf-checked; drop it under the
      // shelf scope rather than leak an unverifiable product into the list.
      if (approvedOnly) {
        if (row.productId == null) continue;
        if (!this.sessionCtx.isOnApprovedShelf(row.productId, row.productType || tab)) continue;
      }
      const company = String(co);
      if (!companyProducts.has(company)) companyProducts.set(company, new Set());
      if (pr) companyProducts.get(company)!.add(String(pr));
    }
    const companyNames = Array.from(companyProducts.keys()).sort();
    const carriers: PickerCarrier[] = companyNames.map((company) => {
      const products: PickerProduct[] = Array.from(companyProducts.get(company)!)
        .sort().map<PickerProduct>((p) => ({ id: this.toPickerProductId(company, p), name: p }));
      return { id: company, name: company, logoText: this.deriveLogoText(company), products };
    });
    return { carriers };
  });

  protected readonly pickerInitialSelection = computed(() => ({
    selectedProductIds: this.currentSelectedProductIds(),
  }));

  private static readonly PICKER_ID_SEP = '::';

  private toPickerProductId(brand: string, product: string): string {
    return `${brand}${RatesRailComponent.PICKER_ID_SEP}${product}`;
  }

  private productNameFromPickerId(id: string): string {
    const idx = id.indexOf(RatesRailComponent.PICKER_ID_SEP);
    return idx === -1 ? id : id.slice(idx + RatesRailComponent.PICKER_ID_SEP.length);
  }

  protected readonly pageSearchText = computed<string>(() => {
    this.filterState.version();
    return this.filterState.searchText ?? '';
  });

  private deriveLogoText(name: string): string {
    const words = name.replace(/[^A-Za-z0-9 ]/g, '').split(/\s+/).filter(Boolean);
    if (words.length === 0) return '??';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  }

  onPickerSelectionChange(r: PickerApplyResult): void {
    // Carrier ids are the actual company names (grouping is no longer
    // brand-aliased), so they go straight into CompanyFilterValue.companies —
    // the grid filter compares those against row.company directly.
    const companies = [...r.selectedCarrierIds];
    // r.selectedProductIds are picker-internal composite ids
    // (`${company}::${productName}`) — strip back to bare names for the
    // CompanyFilterValue, which the grid filter compares against row.product.
    const bareProductNames = [...new Set(r.selectedProductIds.map((id) => this.productNameFromPickerId(id)))];
    const value: CompanyFilterValue | null =
      companies.length === 0 && bareProductNames.length === 0
        ? null : { companies, products: bareProductNames };
    this.filterState.requestCompanyFilter(value);
    // Keep an already-open fly-out in sync — this handler can fire from the
    // left list's own carrier checkbox, which doesn't go through
    // rowClick/reanchor, so the fly-out would otherwise keep showing its
    // stale pre-toggle checkboxes. Pass the composite ids straight through —
    // the fly-out's own picker instance expects the same id format.
    if (this.flyoutSvc.isOpen()) {
      this.flyoutSvc.refreshSelection(r.selectedProductIds);
    }
  }

  protected readonly flyoutCarrierId = signal<string | null>(null);

  /**
   * Row click from the in-rail companies-only picker.
   *
   * The picker now expands the carrier's products inline and owns that state
   * itself, so there is nothing to open here. Kept as the (rowClick) sink
   * because the rail still tracks which carrier is open for its own summary.
   */
  onCarrierRowClick(ev: { carrierId: string; el: HTMLElement }): void {
    this.flyoutCarrierId.set(
      this.flyoutCarrierId() === ev.carrierId ? null : ev.carrierId,
    );
  }

  closeCarrierFlyout(): void {
    if (this.flyoutSvc.isOpen()) this.flyoutSvc.close();
  }

  onPinToggle(dim: string): void {
    const dk = dim as PinDimKey;
    if (this.pinSvc.isPinned(dk)) this.pinSvc.unpin(dk);
    else this.pinSvc.pin(dk);
  }

  onPickerCloseRequest(_dim: string): void { /* no-op */ }

  protected readonly activeAppliedFilters = computed<AppliedFilter[]>(() => {
    this.filterState.version();
    const tab = this.activeTab();
    const slot = this.filterState.activeSlot();
    const pinned = this.pinSvc.pinnedDims();
    const pinnedSet = new Set<string>(pinned);
    const out: AppliedFilter[] = [];
    const push = (dim: DimKey, summary: string, count: number | null) => {
      const pinDim = PANEL_DIM_TO_PIN_DIM[dim];
      const isPinned = pinDim != null && pinnedSet.has(pinDim);
      out.push({ dim, label: DIM_LABELS[dim], summary, count, pinned: isPinned });
    };
    const company = this.filterState.getCompanyFilterForTab(tab);
    if (company && (company.companies.length > 0 || company.products.length > 0)) {
      push('carrier', this.summarizeCarrier(company), company.companies.length + company.products.length);
    }
    if (slot.selectedAmBest.length > 0) push('amBest', this.summarizeMulti(slot.selectedAmBest), slot.selectedAmBest.length);
    if (slot.selectedPremiumTypes.length > 0) push('premiumType', this.summarizeMulti(slot.selectedPremiumTypes), slot.selectedPremiumTypes.length);
    if (slot.selectedIndexes.length > 0) push('index', this.summarizeMulti(slot.selectedIndexes), slot.selectedIndexes.length);
    if (slot.selectedStrategyNames.length > 0) push('crediting', this.summarizeMulti(slot.selectedStrategyNames), slot.selectedStrategyNames.length);
    if (slot.selectedStrategyCombos.length > 0) push('combo', this.summarizeMulti(slot.selectedStrategyCombos), slot.selectedStrategyCombos.length);
    if (slot.selectedCreditingStrategies.length > 0) push('mktg', this.summarizeMulti(slot.selectedCreditingStrategies), slot.selectedCreditingStrategies.length);
    if (slot.contributionAmount != null) push('premium', '$' + slot.contributionAmount.toLocaleString(), null);
    if (slot.selectedSurrYears.length > 0) push('surrYears', this.summarizeMulti(slot.selectedSurrYears), slot.selectedSurrYears.length);
    if (slot.selectedRiders.length > 0) push('riders', this.summarizeMulti(slot.selectedRiders), slot.selectedRiders.length);
    if (slot.selectedStates.length > 0) push('states', this.summarizeStates(slot.selectedStates), slot.selectedStates.length);
    if (slot.filterRop) push('rop', 'on', null);
    if (slot.filterBonus) push('bonus', 'on', null);
    if (slot.filterInverse) push('inverse', 'on', null);
    if (slot.filterOwner) push('owner', 'on', null);
    if (slot.filterAnnuitant) push('annuitant', 'on', null);
    if (tab === 'FA') {
      const faTerms = this.filterState.faRateTerms();
      const faSurr = this.filterState.faSurrYears();
      const faBonus = this.filterState.faBonusYears();
      if (faTerms.length > 0) push('faRateTerm', this.summarizeMulti(faTerms.map(String)), faTerms.length);
      if (faSurr.length > 0) push('faSurrYears', this.summarizeMulti(faSurr.map(String)), faSurr.length);
      if (faBonus.length > 0) push('faBonusYears', this.summarizeMulti(faBonus.map(String)), faBonus.length);
      if (this.filterState.faMva()) push('faMva', 'on', null);
      if (this.filterState.faQualified()) push('faQualified', 'on', null);
    }
    return [...out.filter(r => r.pinned), ...out.filter(r => !r.pinned)];
  });

  protected readonly pinnedInactive = computed<AppliedFilter[]>(() => {
    this.filterState.version();
    const tab = this.activeTab();
    const activeDims = new Set(this.activeAppliedFilters().map(r => r.dim));
    const pinnedForTab = this.pinSvc.applicableForTab(tab);
    const out: AppliedFilter[] = [];
    for (const pinDim of pinnedForTab) {
      const panelDim = reversePinToPanel(pinDim);
      if (!panelDim) continue;
      if (activeDims.has(panelDim)) continue;
      out.push({ dim: panelDim, label: DIM_LABELS[panelDim], summary: 'any', count: null, pinned: true });
    }
    return out;
  });

  clearAll(): void { this.filterState.requestResetAll(); }

  onRowJump(ev: { dim: DimKey; el: HTMLElement }): void {
    this.filterPanel.rememberFocusOrigin(ev.el);
    this.expandGroupContaining(ev.dim);
    queueMicrotask(() => this.scrollDimIntoView(ev.dim));
  }

  constructor() {
    this.loadCaseList();
    effect(() => {
      this.caseCtx.activeCase();
      untracked(() => this.loadCaseList());
    });
    effect(() => {
      const dim = this.filterPanel.focusDimension();
      if (dim === null) return;
      queueMicrotask(() => {
        this.expandGroupContaining(dim);
        this.scrollDimIntoView(dim);
        this.filterPanel.focusDimension.set(null);
      });
    });
    effect(() => {
      this.activeTab();
      this.closeCarrierFlyout();
    });
    effect(() => {
      const carrierOpen = this.isGroupOpen('carrier');
      const filtersOpen = this.filtersOpen();
      const carrierSubOpen = this.carrierSubOpen();
      // Hiding the rail has to close it too. The fly-out is a CDK overlay
      // attached to document.body, NOT a child of the rail, so the rail's own
      // `visibility: hidden` does not reach it -- it would be left floating
      // beside a 44px strip with nothing to explain it.
      const railHidden = this.railLayout.collapsed();
      if (railHidden || !carrierOpen || !filtersOpen || !carrierSubOpen) this.closeCarrierFlyout();
    });
    // Collapse the carrier section and submenu on an explicit "Clear all
    // filters" click. Watches resetAllRequest (not companyFilterRequest) —
    // the latter also fires when the user simply unchecks their last
    // selected carrier from the list, which should leave the section open.
    effect(() => {
      const tick = this.filterState.resetAllRequest();
      if (tick === 0) return;
      this.groupOpen.update(o => ({ ...o, carrier: false }));
      this.carrierSubOpen.set(false);
    });
    // Collapse a section when its Active-Filters chip is cleared (clearDim →
    // dimClearedRequest). This is distinct from unchecking within a picker,
    // which leaves the section open. Carrier → the Carriers sub-accordion;
    // the individual pickers self-collapse (see InlinePickerHostComponent /
    // RailFaChecklistPickerComponent).
    effect(() => {
      const req = this.filterState.dimClearedRequest();
      if (req.tick === 0) return;
      untracked(() => {
        if (req.dim === 'carrier') {
          this.carrierSubOpen.set(false);
          this.closeCarrierFlyout();
        }
      });
    });
  }

  private summarizeMulti(arr: ReadonlyArray<string>): string {
    if (arr.length === 0) return '';
    if (arr.length === 1) return arr[0];
    if (arr.length === 2) return `${arr[0]}, ${arr[1]}`;
    return `${arr[0]} +${arr.length - 1}`;
  }

  private summarizeStates(arr: ReadonlyArray<string>): string {
    if (arr.length === 0) return '';
    if (arr.length === 1) return arr[0];
    if (arr.length === 2) return `${arr[0]}, ${arr[1]}`;
    return `${arr[0]}, ${arr[1]} +${arr.length - 2}`;
  }

  private summarizeCarrier(v: { companies: ReadonlyArray<unknown>; products: ReadonlyArray<unknown> }): string {
    const c = v.companies.length;
    const p = v.products.length;
    if (c === 1 && p === 0) { const first = v.companies[0] as { name?: string } | undefined; return first?.name ?? '1 carrier'; }
    if (c === 0 && p === 1) { const first = v.products[0] as { name?: string } | undefined; return first?.name ?? '1 product'; }
    if (c > 0 && p === 0) return `${c} carrier${c === 1 ? '' : 's'}`;
    if (c === 0 && p > 0) return `${p} product${p === 1 ? '' : 's'}`;
    return `${c} carrier${c === 1 ? '' : 's'} · ${p} product${p === 1 ? '' : 's'}`;
  }
}

function reversePinToPanel(pinDim: string): DimKey | null {
  const map: Record<string, DimKey> = {
    carriers: 'carrier', amBest: 'amBest', premiumTypes: 'premiumType',
    indexes: 'index', strategies: 'crediting', strategyCombos: 'combo',
    premium: 'premium', surrYrs: 'surrYears', faRateTerm: 'faRateTerm',
    faSurrYears: 'faSurrYears', faBonusYears: 'faBonusYears', bonus: 'bonus', inverse: 'inverse', mva: 'mva',
    rop: 'rop', riders: 'riders', states: 'states',
    owner: 'owner', annuitant: 'annuitant',
  };
  return map[pinDim] ?? null;
}
